import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation, Form } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { INDICATOR_URL } from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { handleDeleteItem } from "../../../helpers/handleDeleteItem";
import { IndicatorManagementCombinedSchema } from "../data/IndicatorManagementItemSchema";
import IndicatorSubmitForm from "../forms/IndicatorForms/IndicatorSubmitForm";
import { useAuth } from "../../../provider/AuthProvider";
import TabComponent from "../../../components/molecule/Tab/TabComponent";
import { Flex } from "@chakra-ui/react";
import IndicatorCollectionForm from "../forms/IndicatorForms/IndicatorCollectionForm";
import IndicatorManagementState from "../components/IndicatorManagementState";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";

const EditIndicator: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const location = useLocation();
  const { edit_indicator } = location.state || {};

  const [idItem] = useState<number>(edit_indicator?.id || 1);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState<string>(edit_indicator?.name || "");
  const [isLoaded, setIsLoaded] = useState(true);
  const [hasDuplicateNameError, setHasDuplicateNameError] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    resetField,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(IndicatorManagementCombinedSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const { auth } = useAuth();
  const user = auth.user;

  useEffect(() => {
    if (edit_indicator) {
      setValue("name", edit_indicator.name);
      setValue("indicatorTypeOption", edit_indicator.type);
      setValue("subSystemOption", edit_indicator.subSystem);
      setValue("frequencyOption", edit_indicator.frequency);
      setValue("indicatorMetricOption", edit_indicator.metric);
      setValue("outra", edit_indicator.anotherMetric);

      setValue(
        "healthyLevelOption",
        edit_indicator.reference?.healthySymbol
          ? edit_indicator.reference.healthySymbol
          : undefined,
      );

      setValue(
        "healthyIntervalFrom",
        edit_indicator.reference?.healthySymbol?.id !== 2 &&
          edit_indicator.reference?.healthyLesserValue
          ? edit_indicator.reference.healthyLesserValue
          : undefined,
      );

      setValue(
        "healthyIntervalTo",
        edit_indicator.reference?.healthySymbol?.id === 2 &&
          edit_indicator.reference?.healthyLesserValue
          ? edit_indicator.reference?.healthyLesserValue
          : (edit_indicator.reference?.healthyGreaterValue ?? undefined),
      );

      setValue(
        "unhealthyLevelOption",
        edit_indicator.reference?.unhealthySymbol
          ? edit_indicator.reference.unhealthySymbol
          : undefined,
      );

      setValue(
        "unhealthyIntervalFrom",
        edit_indicator.reference?.unhealthySymbol?.id !== 2 &&
          edit_indicator.reference?.unhealthyLesserValue
          ? edit_indicator.reference.unhealthyLesserValue
          : undefined,
      );

      setValue(
        "unhealthyIntervalTo",
        edit_indicator.reference?.unhealthySymbol?.id === 2 &&
          edit_indicator.reference?.unhealthyLesserValue
          ? edit_indicator.reference?.unhealthyLesserValue
          : (edit_indicator.reference?.unhealthyGreaterValue ?? undefined),
      );

      setValue("indicatorVariationsOption", edit_indicator.readingVariation);
      setValue("indicatorLevelOption", edit_indicator.level);

      setValue(
        "belowAbsoluteLevel",
        edit_indicator.alertState?.lowestValue
          ? edit_indicator.alertState.lowestValue
          : undefined,
      );

      setValue(
        "aboveAbsoluteLevel",
        edit_indicator.alertState?.biggestValue
          ? edit_indicator.alertState.biggestValue
          : undefined,
      );

      setValue(
        "alertVariationCheck",
        edit_indicator.alertState?.considerVariation
          ? edit_indicator.alertState.considerVariation
          : false,
      );

      setValue(
        "superiorAbsoluteValue",
        edit_indicator.alertState?.maxVariationValue
          ? edit_indicator.alertState.maxVariationValue
          : undefined,
      );

      setValue(
        "minPeriodVariation",
        edit_indicator.alertState?.minPeriodVariation
          ? edit_indicator.alertState.minPeriodVariation
          : undefined,
      );

      setValue(
        "maxPeriodVariation",
        edit_indicator.alertState?.maxPeriodVariation
          ? edit_indicator.alertState.maxPeriodVariation
          : undefined,
      );

      setValue(
        "inferiorYearlyLevelVariations",
        edit_indicator.alertState?.minYearVariation
          ? edit_indicator.alertState.minYearVariation
          : undefined,
      );

      setValue(
        "superiorYearlyLevelVariations",
        edit_indicator.alertState?.maxYearVariation
          ? edit_indicator.alertState?.maxYearVariation
          : undefined,
      );

      setValue("partnerOption", edit_indicator.partner);
      setValue("integrationOption", edit_indicator.collectionType);
      setValue("apiKey", edit_indicator.apiKey ? edit_indicator.apiKey : "");
      setValue("state", edit_indicator.state);

      setIsFormInitialized(true);
      setTitle(edit_indicator.name);
    }
  }, [edit_indicator, setValue]);

  const onSubmit = async (data: FieldValues) => {
    const reference = (() => {
      const healthySymbolId = data.healthyLevelOption
        ? data.healthyLevelOption.id
        : null;
      const unhealthySymbolId = data.unhealthyLevelOption
        ? data.unhealthyLevelOption.id
        : null;

      if (healthySymbolId === null && unhealthySymbolId === null) {
        return null;
      }

      return {
        healthySymbolId,
        healthyLesserValue:
          healthySymbolId === 2 && data.healthyIntervalTo
            ? data.healthyIntervalTo
            : healthySymbolId === 4 && data.healthyIntervalFrom
              ? data.healthyIntervalFrom
              : null,
        healthyGreaterValue:
          healthySymbolId !== 2 && data.healthyIntervalTo
            ? data.healthyIntervalTo
            : null,
        unhealthySymbolId,
        unhealthyLesserValue:
          unhealthySymbolId === 2 && data.unhealthyIntervalTo
            ? data.unhealthyIntervalTo
            : unhealthySymbolId === 4 && data.unhealthyIntervalFrom
              ? data.unhealthyIntervalFrom
              : null,
        unhealthyGreaterValue:
          unhealthySymbolId !== 2 && data.unhealthyIntervalTo
            ? data.unhealthyIntervalTo
            : null,
      };
    })();

    const bodyRequest = {
      id: idItem,
      name: data.name,
      typeId: data.indicatorTypeOption.id,
      subSystemId: data.subSystemOption.id,
      frequencyId: data.frequencyOption.id,
      readingVariationId: data.indicatorVariationsOption.id,
      levelId: data.indicatorLevelOption.id,
      alertState: {
        lowestValue: data.belowAbsoluteLevel || null,
        biggestValue: data.aboveAbsoluteLevel || null,
        considerVariation: data.alertVariationCheck || false,
        maxVariationValue: data.superiorAbsoluteValue || null,
        minPeriodVariation: data.minPeriodVariation || null,
        maxPeriodVariation: data.maxPeriodVariation || null,
        minYearVariation: data.inferiorYearlyLevelVariations || null,
        maxYearVariation: data.superiorYearlyLevelVariations || null,
      },
      reference,
      metricId: data.indicatorMetricOption.id,
      anotherMetric: data.outra || "",
      partnerId: data.partnerOption.id,
      indicatorCollectionTypeId: data.integrationOption.id,
    };

    await handleSubmitItem(
      () => api.put(INDICATOR_URL, bodyRequest),
      addToast,
      setIsLoaded,
    );

    navigate("/gestao-de-indicadores");
  };

  const handleDelete = () => {
    handleDeleteItem(
      INDICATOR_URL,
      setIsAlertOpen,
      idItem,
      addToast,
      setIsLoaded,
    );
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SubmitItem
        title={
          <Flex direction="row" gap="4" alignItems="center">
            {title}{" "}
            {edit_indicator?.state && (
              <IndicatorManagementState
                stateValue={edit_indicator.state}
                additionalStyle={{ marginRight: "4" }}
              />
            )}
          </Flex>
        }
        handleSubmitItem={handleSubmit(onSubmit)}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        handleDeleteItem={
          user.permissions.includes("DeleteIndicators")
            ? handleDelete
            : undefined
        }
        deleteButtonName="Eliminar indicador"
        navigatePath="/gestao-de-indicadores"
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
      >
        <TabComponent
          tabs={["Informações do indicador", "Configurar recolha do indicador"]}
          panels={[
            <IndicatorSubmitForm
              errors={errors}
              control={control}
              watch={watch}
              resetField={resetField}
              isDisabled={!isEditing}
              currentStep={0}
              setError={setError}
              clearErrors={clearErrors}
              setHasDuplicateNameError={setHasDuplicateNameError} // ✅ NOVO
              currentId={idItem} // ✅ adiciona aqui
              isFormInitialized={isFormInitialized}
            />,
            <IndicatorCollectionForm
              errors={errors}
              control={control}
              watch={watch}
              isDisabled={!isEditing}
              indicatorState={edit_indicator?.state}
            />,
          ]}
        />
      </SubmitItem>
    </Form>
  );
};

export default EditIndicator;
